/**
 * =============================================================================
 * SCRIPT-NAME:   gsc_status.js
 * PROJEKT:       Freiseinapp - GSC KPI-Dashboard
 * MODUL:         Per-Seite-Statusansicht — wo steht jede LP/LB, Rank & Probleme
 * BESCHREIBUNG:  Liest gsc_get_status.php (Index-Status + Tages-Rank + belastbare
 *                Problem-Flags) und zeigt es als Tabelle, Probleme zuerst.
 * VERSION:       v1.0 · DATUM: 2026-07-08
 * =============================================================================
 */
import React, { useState, useEffect } from 'react';
import BackButton from "./BackButton";
import "./gsc_status.css";

const API_BASE = "https://freiseinapp.de/api";
const PILLAR_COLORS = { VA:'#dc2626', EF:'#0ea5e9', EA:'#16a34a', SO:'#9333ea', PBC:'#d97706' };

function IndexBadge({ p }) {
    if (p.is_indexed === 1) return <span className="gs-badge gs-b-ok">✅ indexiert</span>;
    if (p.is_indexed === null) return <span className="gs-badge gs-b-warn">– n/a</span>;
    const unknown = (p.coverage_state || '').includes('nicht bekannt');
    return <span className="gs-badge gs-b-crit">{unknown ? '🔴 unbekannt' : '🔴 nicht indexiert'}</span>;
}

export default function GscStatus() {
    const token = localStorage.getItem("token");
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [onlyProblems, setOnlyProblems] = useState(false);
    const [days, setDays] = useState(7);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        fetch(`${API_BASE}/gsc_get_status.php?days=${days}`, { headers: { "Authorization": `Bearer ${token}` } })
            .then(r => r.json())
            .then(j => { if (alive) { setData(j); setLoading(false); } })
            .catch(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [days, token]);

    // CSV-Export für Claude Desktop (gsc_export_csv.php, grain status|queries) via Blob+Bearer.
    const downloadCsv = async (grain) => {
        try {
            const res = await fetch(`${API_BASE}/gsc_export_csv.php?grain=${grain}`, { headers: { "Authorization": `Bearer ${token}` } });
            if (!res.ok) { console.error("CSV HTTP", res.status); return; }
            const blob = await res.blob();
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = `gsc_${grain}.csv`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(a.href);
        } catch (e) { console.error("CSV-Export-Fehler:", e); }
    };

    const pages = data?.pages || [];
    const shown = onlyProblems ? pages.filter(p => p.problem_level !== 'ok') : pages;
    const s = data?.summary || {};

    return (
        <div className="gs-wrap">
            <BackButton />
            <h2>🔎 GSC Seiten-Status <small>— wo steht jede LP/LB, Rank &amp; Probleme</small></h2>

            {loading ? <p>lädt…</p> : !data?.success ? <p>Fehler beim Laden.</p> : (
            <>
                <div className="gs-summary">
                    <span className="gs-pill">{s.total} Seiten</span>
                    <span className="gs-pill gs-b-ok">✅ {s.indexed} indexiert</span>
                    <span className="gs-pill gs-b-crit">🔴 {s.critical} kritisch</span>
                    <span className="gs-pill gs-b-warn">🟡 {s.warn} Warnung</span>
                    <span className="gs-pill gs-note">Rank = Ø-Position der letzten {s.window_days} Tage (bis {s.latest_day}) · niedriger = besser</span>
                </div>

                <div className="gs-controls">
                    <label><input type="checkbox" checked={onlyProblems} onChange={e => setOnlyProblems(e.target.checked)} /> nur Probleme zeigen</label>
                    <span className="gs-days">Fenster:&nbsp;
                        {[7, 14, 28].map(d => (
                            <button key={d} className={days === d ? 'active' : ''} onClick={() => setDays(d)}>{d}T</button>
                        ))}
                    </span>
                    <span className="gs-count">{shown.length} angezeigt</span>
                    <span style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button onClick={() => downloadCsv('status')} title="Status-CSV: 1 Zeile je Seite (Index-Status + Problem-Gruppe + Performance). Für Excel/Claude Desktop."
                                style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #1a7f5a', background: '#eafaf3', color: '#1a7f5a', cursor: 'pointer', fontSize: '13px' }}>⬇ Status-CSV</button>
                        <button onClick={() => downloadCsv('queries')} title="Queries-CSV: je (Seite × Suchbegriff) — für Content-/Kannibalisierungs-Analyse."
                                style={{ padding: '4px 10px', borderRadius: '5px', border: '1px solid #1a5a7f', background: '#eaf3fa', color: '#1a5a7f', cursor: 'pointer', fontSize: '13px' }}>⬇ Queries-CSV</button>
                    </span>
                </div>

                <div className="gs-tablewrap">
                <table className="gs-table">
                    <thead><tr>
                        <th>Seite</th><th>Pillar</th><th>Index</th>
                        <th title="Ø Google-Position im Fenster (impressionsgewichtet). Niedriger = besser.">Rank ⓘ</th>
                        <th>Impr</th><th>Klicks</th><th>Probleme (Frühwarnung)</th>
                    </tr></thead>
                    <tbody>
                        {shown.map(p => (
                            <tr key={p.prefix + p.url} className={`gs-row gs-row-${p.problem_level}`}>
                                <td className="gs-seite">
                                    <strong>{p.prefix}</strong>
                                    <a href={p.url} target="_blank" rel="noopener noreferrer" className="gs-link" title={p.url}>↗</a>
                                </td>
                                <td>{p.pillar ? <span className="gs-pillar" style={{ background: PILLAR_COLORS[p.pillar] || '#888' }}>{p.pillar}</span> : ''}</td>
                                <td><IndexBadge p={p} /></td>
                                <td className="gs-rank">{p.rank !== null ? String(p.rank).replace('.', ',') : '–'}</td>
                                <td>{p.impressions}</td>
                                <td>{p.clicks}</td>
                                <td className="gs-probs">{p.problems.join(' · ')}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                </div>
                <p className="gs-foot">Datenquellen: <code>gsc_url_inspection</code> (Index/Crawl, tägl. 04:10) + <code>gsc_daily_data</code> (Rank/Impr, tägl. 04:00). Rank-Deltas &amp; „0 Impr." sind bei dem Volumen bewusst KEINE Flags (Rauschen) — belastbar sind Index-/Crawl-Signale.</p>
            </>
            )}
        </div>
    );
}
